import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import {
  FaJava, FaDocker, FaGitAlt, FaReact, FaAngular, FaNodeJs, FaPython, FaAws,
} from "react-icons/fa";
import {
  SiSpringboot, SiKubernetes, SiMongodb, SiPostgresql, SiRedis, SiRedux,
  SiNextdotjs, SiTailwindcss, SiTypescript, SiNestjs, SiFastapi, SiApachekafka,
  SiFlutter, SiGithubactions, SiGraphql,
} from "react-icons/si";
import { TbTopologyStar3 } from "react-icons/tb";
import { colors, font, pagePx } from "../theme/tokens";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

const categories = [
  {
    title: "Architecture & Systems",
    accent: colors.primary,
    skills: [
      { name: "Microservices", icon: <TbTopologyStar3 /> },
      { name: "Event-Driven / CQRS", icon: <SiApachekafka /> },
      { name: "Apache Kafka", icon: <SiApachekafka /> },
      { name: "Design Patterns", icon: null },
      { name: "REST / API-First", icon: null },
      { name: "Domain-Driven Design", icon: null },
      { name: "Distributed Systems", icon: null },
    ],
  },
  {
    title: "Backend & Data",
    accent: colors.accent,
    skills: [
      { name: "TypeScript", icon: <SiTypescript /> },
      { name: "NestJS", icon: <SiNestjs /> },
      { name: "Node.js", icon: <FaNodeJs /> },
      { name: "Python", icon: <FaPython /> },
      { name: "FastAPI", icon: <SiFastapi /> },
      { name: "Java / Spring Boot", icon: <SiSpringboot /> },
      { name: "PostgreSQL", icon: <SiPostgresql /> },
      { name: "MongoDB", icon: <SiMongodb /> },
      { name: "Redis", icon: <SiRedis /> },
      { name: "GraphQL", icon: <SiGraphql /> },
    ],
  },
  {
    title: "Frontend & Mobile",
    accent: colors.green,
    skills: [
      { name: "React", icon: <FaReact /> },
      { name: "Next.js", icon: <SiNextdotjs /> },
      { name: "Redux Toolkit", icon: <SiRedux /> },
      { name: "Angular", icon: <FaAngular /> },
      { name: "Tailwind CSS", icon: <SiTailwindcss /> },
      { name: "Flutter", icon: <SiFlutter /> },
    ],
  },
  {
    title: "DevOps & Cloud",
    accent: colors.amber,
    skills: [
      { name: "Docker", icon: <FaDocker /> },
      { name: "Kubernetes", icon: <SiKubernetes /> },
      { name: "CI/CD", icon: <SiGithubactions /> },
      { name: "GitHub Actions", icon: <SiGithubactions /> },
      { name: "Git", icon: <FaGitAlt /> },
      { name: "Linux / Nginx", icon: null },
      { name: "Observability (Grafana)", icon: null },
    ],
  },
];

const Skills = () => {
  return (
    <Box component="section" id="skills" sx={{ px: pagePx, py: { xs: 8, md: 12 }, position: "relative" }}>
      <SectionHeading
        eyebrow="Skills"
        title="Technical toolkit"
        subtitle="Organized the way I actually use it — architecture first, then the stack that brings it to life."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          maxWidth: 1100,
          mx: "auto",
        }}
      >
        {categories.map((cat, ci) => (
          <Reveal key={cat.title} delay={ci * 80}>
            <Box
              sx={{
                height: "100%",
                p: 3,
                borderRadius: "16px",
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: cat.accent }} />
                <Typography sx={{ fontFamily: font.sans, fontWeight: 600, fontSize: 17, color: colors.ink }}>
                  {cat.title}
                </Typography>
              </Stack>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {cat.skills.map((s) => (
                  <Box
                    key={s.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.7,
                      fontSize: "13px",
                      fontWeight: 500,
                      color: colors.inkSoft,
                      backgroundColor: "rgba(255,255,255,0.03)",
                      border: `1px solid ${colors.border}`,
                      borderRadius: "9px",
                      px: 1.3,
                      py: 0.7,
                      transition: "all 0.2s ease",
                      "&:hover": { color: colors.ink, borderColor: cat.accent, backgroundColor: "rgba(255,255,255,0.06)" },
                      "& svg": { fontSize: 15, color: cat.accent },
                    }}
                  >
                    {s.icon}
                    {s.name}
                  </Box>
                ))}
              </Box>
            </Box>
          </Reveal>
        ))}
      </Box>
    </Box>
  );
};

export default Skills;
